import React, { useEffect, useState } from 'react';
import { broadcastsApi } from '../lib/api';
import { Megaphone, Plus, Send, Ban } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

export default function Broadcasts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    text: '',
    filter: { type: 'all' },
  });
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await broadcastsApi.getAll();
      setItems(res.data || []);
    } catch {
      toast.error('Ошибка загрузки рассылок');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doPreview = async () => {
    try {
      const res = await broadcastsApi.preview(form);
      setPreview(res.data);
      toast.success(`Получателей: ${res.data.total_recipients}`);
    } catch {
      toast.error('Ошибка превью');
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await broadcastsApi.create(form);
      toast.success('Рассылка создана');
      setOpen(false);
      setForm({ name: '', text: '', filter: { type: 'all' } });
      setPreview(null);
      load();
    } catch {
      toast.error('Ошибка создания');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-zinc-400">Загрузка…</div>;

  return (
    <div data-testid="broadcasts-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-orange-500/10 flex items-center justify-center">
            <Megaphone className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Рассылки</h1>
            <p className="text-sm text-zinc-500">Массовые сообщения подписчикам</p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#2AABEE]" data-testid="broadcast-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Создать
        </Button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <p className="text-zinc-500">Рассылок пока нет.</p>}
        {items.map((b) => (
          <div key={b.id} className="p-4 rounded-xl border border-white/5 bg-[#0a0a0a]" data-testid={`broadcast-row-${b.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-white">{b.name}</h3>
                <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{b.text}</p>
                <p className="text-xs text-zinc-500 mt-2">
                  Статус: {b.status} · отправлено {b.sent_count || 0}/{b.total_recipients || 0}
                  {b.blocked_count ? ` · блок ${b.blocked_count}` : ''}
                </p>
              </div>
              <div className="flex gap-2">
                {b.status !== 'running' && b.status !== 'completed' && (
                  <Button
                    size="sm"
                    className="bg-emerald-600"
                    onClick={async () => {
                      if (!window.confirm('Запустить рассылку?')) return;
                      try {
                        await broadcastsApi.send(b.id);
                        toast.success('Рассылка запущена');
                        load();
                      } catch {
                        toast.error('Не удалось запустить');
                      }
                    }}
                  >
                    <Send className="w-3 h-3 mr-1" /> Старт
                  </Button>
                )}
                {b.status === 'running' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      await broadcastsApi.cancel(b.id);
                      toast.success('Отмена');
                      load();
                    }}
                  >
                    <Ban className="w-3 h-3 mr-1" /> Стоп
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Новая рассылка</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label>Название</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#121212] border-zinc-800"
                required
              />
            </div>
            <div>
              <Label>Текст (HTML)</Label>
              <textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="w-full min-h-[120px] rounded-md bg-[#121212] border border-zinc-800 p-2 text-sm"
                required
              />
            </div>
            <div>
              <Label>Фильтр</Label>
              <Select
                value={form.filter.type}
                onValueChange={(v) => setForm({ ...form, filter: { type: v } })}
              >
                <SelectTrigger className="bg-[#121212] border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все пользователи</SelectItem>
                  <SelectItem value="active">Только активные подписки</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {preview && (
              <p className="text-sm text-emerald-400">Получателей: {preview.total_recipients}</p>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={doPreview}>Превью</Button>
              <Button type="submit" disabled={submitting} className="bg-[#2AABEE]">
                Создать
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
